import React, { useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CssBaseline,
    Chip,
    Button,
    IconButton
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import BadgeIcon from '@mui/icons-material/Badge';
import DevicesIcon from '@mui/icons-material/Devices';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import logoWhite from '../assets/images/logo-white.svg';

const drawerWidth = 240;
const topBarHeight = 70;

const Dashboard = () => {
    const { auth, logout } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const menuItems = [
        { text: 'Afiliaciones', path: 'affiliations', icon: <BadgeIcon />, admin: false },
        { text: 'Comercios', path: 'merchants', icon: <StoreIcon />, admin: false },
        { text: 'Terminales', path: 'terminals', icon: <DevicesIcon />, admin: false },
        { text: 'Monitoreo Copayment', path: 'copayment-logs', icon: <MonitorHeartIcon />, admin: true },
        { text: 'Usuarios', path: 'users', icon: <PeopleIcon />, admin: true },
    ];

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: isSidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
                    ml: isSidebarOpen ? `${drawerWidth}px` : 0,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: 'none',
                    borderBottom: (theme) => `1px solid ${theme.palette.primary.dark}`,
                }}
            >
                <Toolbar
                    sx={{
                        height: `${topBarHeight}px`,
                        minHeight: `${topBarHeight}px !important`,
                        px: '16px !important',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
                        <IconButton
                            color="inherit"
                            onClick={() => setIsSidebarOpen((prev) => !prev)}
                            sx={{ mr: 1 }}
                            aria-label={isSidebarOpen ? 'Ocultar menú lateral' : 'Mostrar menú lateral'}
                        >
                            {isSidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
                        </IconButton>
                        Panel administrativo
                    </Typography>
                </Toolbar>
            </AppBar>
            {isSidebarOpen && (
                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                        },
                    }}
                    variant="permanent"
                    anchor="left"
                >
                    <Toolbar
                        sx={{
                            height: '71px !important',
                            minHeight: '71px !important',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 1.5,
                            px: '16px !important',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderBottom: (theme) => `1px solid ${theme.palette.primary.dark}`,
                        }}
                    >
                        <Box component="img" src={logoWhite} alt="LANDI" sx={{ height: 30, display: 'block' }} />
                    </Toolbar>
                    <List>
                        {menuItems.map((item) => {
                            if (item.admin && auth?.role !== 'admin') {
                                return null;
                            }

                            const isSelected = location.pathname.includes(`/dashboard/${item.path}`);

                            return (
                                <ListItem key={item.text} disablePadding sx={{ px: 1, py: 0.25 }} component={RouterLink} to={item.path}>
                                    <ListItemButton
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: 1.5,
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                    <Box sx={{ mt: 'auto', p: 2 }}>
                        <Chip
                            label={auth?.username || 'Sesión activa'}
                            variant="outlined"
                            size="small"
                            sx={{ width: '100%', justifyContent: 'flex-start' }}
                        />
                        <Button
                            fullWidth
                            variant="outlined"
                            color="inherit"
                            startIcon={<LogoutIcon />}
                            onClick={handleLogout}
                            sx={{ mt: 1.5 }}
                        >
                            Cerrar sesión
                        </Button>
                    </Box>
                </Drawer>
            )}
            <Box
                component="main"
                sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}
            >
                <Toolbar sx={{ height: `${topBarHeight}px`, minHeight: `${topBarHeight}px !important` }} />
                <Outlet />
            </Box>
        </Box>
    );
};

export default Dashboard;
